import { Block, Delete, Print } from "@mui/icons-material";
import { Box, Button, Chip, ListItemText, Menu, MenuItem, MenuList, Tooltip, Typography } from '@mui/material';
import { AssociacaoByStatus, StatusNotaFiscal } from 'pages/Venda/vendas.model';
import { useState } from "react";
import { EntradaSaida, EntradaSaidaDescritivo } from 'utils/types';
import { FlowConsignadoVisualizarNotaBaseDTO } from '../movimentacoes/movimentacoes.model';
import './mini-nota.style.css';
import { theme } from "theme";

type Props = {
    data: FlowConsignadoVisualizarNotaBaseDTO;
    index: number;
    onClick: () => void;
    onDelete?: (item: FlowConsignadoVisualizarNotaBaseDTO) => void;
    onCancelar?: (item: FlowConsignadoVisualizarNotaBaseDTO) => void;
    onImprimir?: (item: FlowConsignadoVisualizarNotaBaseDTO) => void;
    onImprimirDanfe?: (item: FlowConsignadoVisualizarNotaBaseDTO) => void;
};

const MiniNota = (props: Props) => {
    const color1 = theme.palette.primary.light;
    const color2 = theme.palette.primary.main;

    const width = 100;
    const borderSize = 7;
    const dobra = width * 0.3;

    const index = props.index;
    const numCols = 6;

    const row = Math.floor(index / numCols);
    const col = index % numCols;
    const diagonal = row + col;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleCloseMenuImprimir = () => {
        setAnchorEl(null);
    };

    const shouldShowImprimirDanfe = props.data.situacao === StatusNotaFiscal.AUTORIZADA && !!props.onImprimirDanfe;
    const shouldShowImprimirRomaneio = props.data.tipo === EntradaSaida.SAIDA && !!props.onImprimir;
    const shouldShowDelete = props.data.situacao === StatusNotaFiscal.REJEITADA && !!props.onDelete;
    const shouldShowCancelar = props.data.situacao === StatusNotaFiscal.AUTORIZADA && !!props.onCancelar;

    const corInfo = AssociacaoByStatus[props.data.situacao];

    return (
        <div
            style={{
                '--delay': `${diagonal}`,
                paddingTop: `${dobra + borderSize + 10}px`,
                width: 'min-content'
            } as React.CSSProperties & Record<string, any>}
            className="flipAppear"
        >
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: '100%',
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    gap: '7px',
                    paddingBottom: '7px',
                }}
            >
                <OptionButton
                    id='imprimir'
                    tooltip="Imprimir"
                    visible={shouldShowImprimirDanfe || shouldShowImprimirRomaneio}
                    onClick={e => {
                        setAnchorEl(e.currentTarget);
                    }}
                    icon={
                        <Print sx={{ color: theme.palette.success.main }} />
                    }
                />

                <Menu key="menu_row"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenuImprimir}
                >
                    <MenuList>
                        {shouldShowImprimirDanfe &&
                            <MenuItem
                                onClick={() => {
                                    handleCloseMenuImprimir();
                                    props.onImprimirDanfe?.(props.data);
                                }}
                            >
                                <ListItemText>Danfe</ListItemText>
                            </MenuItem>
                        }

                        {shouldShowImprimirRomaneio &&
                            <MenuItem
                                onClick={() => {
                                    handleCloseMenuImprimir();
                                    props.onImprimir?.(props.data);
                                }}
                            >
                                <ListItemText>Romaneio</ListItemText>
                            </MenuItem>
                        }
                    </MenuList>
                </Menu>

                <OptionButton
                    id='excluir'
                    tooltip="Excluir"
                    visible={shouldShowDelete}
                    onClick={() => {
                        props.onDelete?.(props.data);
                    }}
                    icon={
                        <Delete sx={{ color: theme.palette.error.main }} />
                    }
                />

                <OptionButton
                    id='cancelar'
                    tooltip="Cancelar"
                    visible={shouldShowCancelar}
                    onClick={() => {
                        props.onCancelar?.(props.data);
                    }}
                    icon={
                        <Block sx={{ color: theme.palette.error.main }} />
                    }
                />
            </Box>

            <Box
                onClick={props.onClick}
                sx={{
                    background: theme.palette.background.default,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '10px',
                    position: 'relative',
                    width: `${width}px`,
                    height: `${(width * 1.414) - dobra + (borderSize * 2)}px`,
                    borderLeft: `${borderSize}px solid ${color1}`,
                    borderRight: `${borderSize}px solid ${color2}`,
                    borderBottom: `${borderSize}px solid ${color2}`,
                    transition: '.3s ease',
                    boxShadow: '2px 2px 2px rgba(0, 0, 0, .20)',
                    '&:before': {
                        content: '""',
                        display: 'flex',
                        position: 'absolute',
                        bottom: '100%',
                        left: `-${borderSize}px`,
                        height: `${dobra}px`,
                        width: `calc(100% - ${dobra}px)`,
                        borderLeft: `${borderSize}px solid ${color1}`,
                        borderTop: `${borderSize}px solid ${color1}`,
                    },
                    '&:after': {
                        content: '""',
                        display: 'flex',
                        position: 'absolute',
                        bottom: '100%',
                        right: `-${borderSize}px`,
                        height: '0px',
                        width: '0',
                        borderLeft: `${dobra + borderSize}px solid ${color2}`,
                        borderTop: `${dobra + borderSize}px solid transparent`,
                        boxShadow: `-${borderSize}px ${borderSize}px 0 0px ${color1}`
                    },
                    '&:hover': {
                        transform: 'scale(1.07)',
                        transition: '.1s ease-out',
                        boxShadow: '5px 5px 4px rgba(0, 0, 0, .15)',

                    }
                }} >
                <Chip
                    label={EntradaSaidaDescritivo[props.data.tipo]}
                    sx={{
                        position: 'absolute',
                        top: -((dobra / 2) + (borderSize / 2)),
                        left: '8px',
                        color: theme.palette.background.default,
                        background: props.data.tipo === 'SAIDA' ? theme.palette.success.main : theme.palette.error.main,
                        width: 'min-content',
                        fontSize: '11px',
                        height: '18px',
                        '& > span': {
                            paddingX: '6px',
                        }
                    }}
                />

                <Box sx={{
                    display: 'flex',
                    gap: '5px',
                }}>
                    <Typography
                        color={corInfo.rgb2 ?? corInfo.rgb}
                        fontWeight={'900'}
                        letterSpacing={'1px'}
                        fontSize={10}
                    >
                        {props.data.situacao}
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    gap: '5px',
                }}>
                    <Typography
                        color={theme.palette.text.primary}
                        fontWeight={'700'}
                        fontSize={13}
                    >
                        Série:
                    </Typography>
                    <Typography
                        color={theme.palette.text.primary}
                        fontSize={13}
                    >
                        {props.data.serie}
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    gap: '5px',
                }}>
                    <Typography
                        color={theme.palette.text.primary}
                        fontWeight={'700'}
                        fontSize={13}
                    >
                        Nº:
                    </Typography>
                    <Typography
                        color={theme.palette.text.primary}
                        fontSize={13}
                    >
                        {props.data.numero}
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    justifyContent: 'space-between'
                }}>
                    <Typography
                        color={theme.palette.text.primary}
                        fontWeight={'700'}
                        fontSize={13}
                    >
                        Emissão:
                    </Typography>
                    <Typography
                        color={theme.palette.text.primary}
                        fontSize={13}
                    >
                        {props.data.emissao}
                    </Typography>
                </Box>
            </Box >
        </div >
    );
};

export default MiniNota;

const OptionButton = (props: {
    id: string;
    visible?: boolean;
    onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    icon: JSX.Element;
    tooltip: string;
}) => {
    if (props.visible === false) {
        return <></>;
    }

    return <Tooltip
        title={props.tooltip}
        placement='right'
        arrow
    >
        <Button
            id={`${props.id}-btn`}
            sx={{
                width: 'min-content',
                background: theme.palette.background.default,
                borderRadius: '0 50px 50px 0',
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '3px 5px 5px',
                boxShadow: '1px 1px 2px rgba(0,0,0, .15)',
                minWidth: '0'
            }}
            onClick={(e) => props.onClick(e)}
        >
            {props.icon}
        </Button>
    </Tooltip>;
};
